/**
 * NOS-48, NOS-49: Public event detail at /events/:eventSlug or /:orgSlug/events/:eventSlug.
 * Renders event description via BlockRenderer, RSVP form linking to contacts.
 */

import { useEffect, useState, useCallback, useMemo } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { PublicSitePage } from "@/components/PublicSitePage"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { resolveEventHead } from "@/lib/seo/headData"
import { eventJsonLd as eventJsonLdGen } from "@/lib/seo/structuredData"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import { BlockRenderer } from "@/components/website-builder/BlockRenderer"
import type { Organization, OrgCustomizations, PageSection } from "@/templates/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowLeft, Calendar, MapPin, Video, Loader2 } from "lucide-react"
import { FunctionsHttpError } from "@supabase/supabase-js"
import type { Json } from "@/types/database"
import {
  EventRegistrationForm,
  type EventRegistrationTicketType,
} from "@/components/public/EventRegistrationForm"

interface EventRow {
  id: string
  org_id: string
  title: string
  slug: string
  description_sections: Json
  start_date: string
  end_date: string | null
  location_name: string | null
  location_address: string | null
  location_type: "in_person" | "virtual" | "hybrid" | null
  virtual_url: string | null
  cover_image: string | null
  registration_enabled: boolean
  waitlist_enabled: boolean
  registration_deadline: string | null
  capacity: number | null
  /** NOS-249: per-event SEO overrides, editable in the event editor */
  seo_title?: string | null
  seo_description?: string | null
  og_image?: string | null
}

interface EventDetailPageProps {
  orgSlug: string
  eventSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

function formatEventDate(start: string, end: string | null): string {
  const startDate = new Date(start)
  const opts: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }
  if (end) {
    const endDate = new Date(end)
    return `${startDate.toLocaleDateString("en-US", opts)} – ${endDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
  }
  return startDate.toLocaleDateString("en-US", opts)
}

function descriptionToSections(desc: Json, orgId: string): PageSection[] {
  const arr = Array.isArray(desc) ? desc : []
  return (arr as Record<string, unknown>[]).map((item, i) => ({
    id: String(item.id ?? crypto.randomUUID()),
    section_type: String(item.section_type ?? "paragraph"),
    order_index: typeof item.order_index === "number" ? item.order_index : i,
    content: (item.content as Record<string, unknown>) ?? null,
    page_id: "",
    org_id: orgId,
    is_visible: true,
  }))
}

export function EventDetailPage({
  orgSlug,
  eventSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: EventDetailPageProps) {
  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org, cust, primaryColor } = shell

  const [event, setEvent] = useState<EventRow | null>(null)
  const [ticketTypes, setTicketTypes] = useState<EventRegistrationTicketType[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [rsvpEmail, setRsvpEmail] = useState("")
  const [rsvpName, setRsvpName] = useState("")
  const [rsvpGuests, setRsvpGuests] = useState("")
  const [rsvpLoading, setRsvpLoading] = useState(false)
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false)
  const [rsvpError, setRsvpError] = useState<string | null>(null)

  const orgId = org?.id
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!eventSlug) {
        setLoading(false)
        setNotFound(true)
        return
      }
      if (!orgId) return

      const { data: eventData, error: eventErr } = await supabase
        .from("events")
        .select("*")
        .eq("org_id", orgId)
        .eq("slug", eventSlug)
        .eq("is_published", true)
        .maybeSingle()

      if (cancelled) return
      if (eventErr || !eventData) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const evRow = eventData as EventRow
      setEvent(evRow)

      if (evRow.registration_enabled) {
        const { data: ttData } = await supabase
          .from("event_ticket_types")
          .select("id, name, seats_per_ticket, price_cents")
          .eq("event_id", evRow.id)
          .eq("is_active", true)
          .order("sort_order", { ascending: true })
        if (!cancelled) {
          setTicketTypes((ttData ?? []) as EventRegistrationTicketType[])
        }
      } else if (!cancelled) {
        setTicketTypes([])
      }

      if (!cancelled) setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgId, eventSlug])

  const sectionBlocks = useMemo(() => {
    if (!event) return []
    return descriptionToSections(event.description_sections ?? [], event.org_id)
  }, [event])

  // NOS-249: title/description/canonical/og:image resolution moved into the
  // shared renderer-agnostic resolver (explicit override → body/cover →
  // generated branded default).
  const headData = useMemo(() => {
    if (!event || !org) return null
    return resolveEventHead({ org: org, event, sections: sectionBlocks })
  }, [event, org, sectionBlocks])

  // NOS-242: regenerated Event JSON-LD via the shared generator — adds
  // url, eventAttendanceMode, and ticket offers (M14 ticketing prices).
  const eventJsonLd = useMemo(() => {
    if (!event || !org || !headData) return null
    const canonicalUrl = headData.canonicalUrl
    return eventJsonLdGen({
      title: event.title,
      url: canonicalUrl,
      description: headData.description,
      startDate: event.start_date,
      endDate: event.end_date,
      imageUrl: headData.ogImage,
      locationType: event.location_type,
      virtualUrl: event.virtual_url,
      locationName: event.location_name,
      locationAddress: event.location_address,
      orgName: org.name,
      orgUrl: getPublicSiteOrigin(org),
      offers: event.registration_enabled
        ? ticketTypes.map((t) => ({
            name: t.name,
            priceCents: t.price_cents,
            url: canonicalUrl,
          }))
        : null,
    })
  }, [event, org, headData, ticketTypes])

  const handleRsvp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!event || !org || rsvpLoading || rsvpSubmitted) return

      const email = rsvpEmail.trim()
      if (!email) {
        setRsvpError("Email is required")
        return
      }

      setRsvpLoading(true)
      setRsvpError(null)

      try {
        const { error: fnError } = await supabase.functions.invoke("rsvp-event", {
          body: {
            org_id: org.id,
            event_id: event.id,
            email,
            name: rsvpName.trim() || undefined,
            guest_count: rsvpGuests ? Math.min(Math.max(0, parseInt(rsvpGuests, 10) || 0), 99) : 0,
          },
        })

        if (fnError) {
          let message = fnError.message ?? "Failed to register"
          if (fnError instanceof FunctionsHttpError && fnError.context) {
            try {
              const res = fnError.context as Response
              const body = (await res.json()) as { error?: string }
              if (typeof body?.error === "string") message = body.error
            } catch {
              // keep default
            }
          }
          setRsvpError(message)
          return
        }
        setRsvpSubmitted(true)
      } catch (err) {
        setRsvpError(err instanceof Error ? err.message : "An unexpected error occurred")
      } finally {
        setRsvpLoading(false)
      }
    },
    [event, org, rsvpEmail, rsvpName, rsvpGuests, rsvpLoading, rsvpSubmitted]
  )

  if (notFound || shell.notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Event not found.</p>
      </div>
    )
  }

  if (loading || shell.loading || !event || !org) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const isPastEvent = new Date(event.start_date) < new Date()

  return (
    <PublicSitePage
      shell={shell}
      head={
        headData ? (
          <SEOHead
            title={headData.title}
            description={headData.description}
            canonicalUrl={headData.canonicalUrl}
            ogType={headData.ogType}
            imageUrl={headData.ogImage}
            jsonLd={eventJsonLd}
          />
        ) : null
      }
    >
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <Link
          to={`${basePath}/events`}
          className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to events
        </Link>

        <article className="space-y-8">
          {event.cover_image && (
            <div className="aspect-video overflow-hidden rounded-lg">
              <img
                src={event.cover_image}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
          )}

          <header>
            <h1 className="text-4xl font-bold" style={{ color: primaryColor }}>
              {event.title}
            </h1>
            <div className="mt-4 space-y-2 text-muted-foreground">
              <p className="flex items-center gap-2">
                <Calendar className="h-5 w-5 shrink-0" />
                {formatEventDate(event.start_date, event.end_date)}
              </p>
              {event.location_name && (
                <p className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 shrink-0" />
                  {event.location_name}
                  {event.location_address && ` • ${event.location_address}`}
                </p>
              )}
              {(event.location_type === "virtual" || event.location_type === "hybrid") &&
                event.virtual_url && (
                  <a
                    href={event.virtual_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <Video className="h-5 w-5 shrink-0" />
                    Join virtually
                  </a>
                )}
            </div>
          </header>

          {sectionBlocks.length > 0 && (
            // NOS-221: no `prose` wrapper around section blocks (see BlogPostPage)
            <div className="max-w-none">
              {sectionBlocks.map((section) => (
                <BlockRenderer
                  key={section.id}
                  section={section}
                  customizations={cust}
                  org={org}
                />
              ))}
            </div>
          )}

          {!isPastEvent && event.registration_enabled && (
            <EventRegistrationForm
              orgId={org.id}
              event={event}
              ticketTypes={ticketTypes}
              primaryColor={primaryColor}
            />
          )}
          {!isPastEvent && !event.registration_enabled && (
            <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
              <CardContent className="p-6">
                {rsvpSubmitted ? (
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold" style={{ color: primaryColor }}>
                      You&apos;re registered!
                    </h3>
                    <p className="text-muted-foreground">
                      We&apos;ve saved your spot. Check your email for event details.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="mb-4 text-xl font-semibold" style={{ color: primaryColor }}>
                      RSVP for this event
                    </h3>
                    <form onSubmit={handleRsvp} className="space-y-4 max-w-md">
                      <div className="space-y-2">
                        <Label htmlFor="rsvp-email">
                          Email <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="rsvp-email"
                          type="email"
                          value={rsvpEmail}
                          onChange={(e) => setRsvpEmail(e.target.value)}
                          required
                          placeholder="you@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rsvp-name">Name (optional)</Label>
                        <Input
                          id="rsvp-name"
                          type="text"
                          value={rsvpName}
                          onChange={(e) => setRsvpName(e.target.value)}
                          placeholder="Your name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rsvp-guests">Number of guests (optional)</Label>
                        <Input
                          id="rsvp-guests"
                          type="number"
                          min={0}
                          max={99}
                          value={rsvpGuests}
                          onChange={(e) => setRsvpGuests(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      {rsvpError && (
                        <p className="text-sm text-destructive" role="alert">
                          {rsvpError}
                        </p>
                      )}
                      <Button
                        type="submit"
                        disabled={rsvpLoading}
                        style={{ backgroundColor: primaryColor }}
                      >
                        {rsvpLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Registering...
                          </>
                        ) : (
                          "Register"
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </article>
      </main>
    </PublicSitePage>
  )
}
